import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api, readBlobError } from '../api/client';
import PaymentDetailsBlock from './PaymentDetailsBlock';

interface EventPaymentQRProps {
  eventId: string;
  size?: number;
}

const EventPaymentQR: React.FC<EventPaymentQRProps> = ({ eventId, size = 200 }) => {
  const { t } = useTranslation();
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let blobUrl: string | null = null;

    const fetchQR = async () => {
      try {
        const response = await api.payments.eventQr(eventId);
        blobUrl = URL.createObjectURL(response.data as Blob);
        setQrUrl(blobUrl);
        setError(false);
        setErrorCode(null);
      } catch (err) {
        const { code } = await readBlobError(err);
        setErrorCode(code ?? null);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchQR();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [eventId]);

  if (loading) {
    return (
      <div style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px',
        color: '#666',
        fontSize: '0.8rem'
      }}>
        Loading...
      </div>
    );
  }

  if (error || !qrUrl) {
    return (
      <div style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px',
        color: '#666',
        fontSize: '0.75rem',
        textAlign: 'center',
        padding: '10px'
      }}>
        {/* Admin-only component: show the actionable message for the no-bank case. */}
        {errorCode === 'BANK_NOT_CONFIGURED'
          ? t('payments.qrBankNotConfiguredAdmin')
          : t('payments.qrUnavailable')}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: size, width: '100%' }}>
      <img
        src={qrUrl}
        alt="Event Payment QR"
        style={{ width: '100%', maxWidth: size, aspectRatio: '1 / 1', display: 'block' }}
      />
      <PaymentDetailsBlock type="event-generic" entityId={eventId} isAdmin />
    </div>
  );
};

export default EventPaymentQR;
